import * as yup from 'yup'
import type { Database } from '../types/supabase'
import {
  nullableNumberValidator,
  nullableStringValidator,
  commonFields,
  type InputType,
  type UploadType,
  type EmptyInputType,
  type UploadWithNewReferenceType,
} from './common'
import { KNOWN_MATERIALS } from './constants'

type ProteinContentInput = Omit<
  InputType<ProteinContentUpload>,
  'material_id'
> & { material?: string | null }

type EmptyProteinContentInput = EmptyInputType<ProteinContentInput>

type ProteinContentTable =
  Database['public']['Tables']['protein_content']['Insert']

type ProteinContentUpload = UploadType<ProteinContentTable>

type ProteinContentPreUpload = UploadWithNewReferenceType<ProteinContentUpload>

export type ProteinContentTypes = {
  upload: ProteinContentUpload
  withNewReference: ProteinContentPreUpload
  input: ProteinContentInput
  emptyInput: EmptyProteinContentInput
}

export const schema: yup.ObjectSchema<ProteinContentInput> = yup
  .object({
    ...commonFields,
    material: nullableStringValidator.oneOf(
      KNOWN_MATERIALS,
      `Material must be one of the known materials: ${KNOWN_MATERIALS.join(
        ', ',
      )}`,
    ),
    moisture_status: nullableStringValidator,
    cultivar: nullableStringValidator,
    notes: nullableStringValidator,
    protein_content: nullableNumberValidator,
  })
  .test(
    'reference-id-or-new-reference-exists',
    'Reference ID or new reference must be provided',
    function (value) {
      if (
        value.reference_id ??
        (value.new_reference_citation && value.new_reference_description)
      ) {
        return true
      }
      return false
    },
  )

export const transform = (
  data: Partial<ProteinContentInput>,
): Partial<ProteinContentPreUpload> => {
  const { material, ...rest } = data

  const material_id = material
    ? KNOWN_MATERIALS.findIndex((m) => m === material) + 1
    : null

  return {
    ...rest,
    material_id,
  }
}
